import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle, Zap, Brain, Shield, TrendingUp, Globe, Layers } from 'lucide-react';

const Products = () => {
  const [activeProduct, setActiveProduct] = useState('platform');
  const location = useLocation();

  // Effect to handle URL hash navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['platform', 'marketplace', 'financial', 'analytics'].includes(hash)) {
      setActiveProduct(hash);
    }
  }, [location.hash]);

  const products = {
    platform: {
      title: 'Agent-Driven Commerce Platform',
      subtitle: 'The Core Intelligence Engine',
      description: 'Revolutionary platform that orchestrates complex multi-party transactions through autonomous AI agents.',
      icon: Brain,
      features: [
        {
          title: 'Autonomous Decision Making',
          description: 'AI agents make complex decisions without human intervention, managing unlimited transaction complexity.',
          capabilities: [
            'Real-time decision processing across multiple variables',
            'Autonomous contract negotiation and execution',
            'Dynamic resource allocation based on priorities',
            'Predictive problem resolution before issues arise'
          ]
        },
        {
          title: 'Multi-Party Orchestration', 
          description: 'Seamlessly coordinate transactions involving unlimited parties across global operations.',
          capabilities: [
            'Complex deal structuring with multiple stakeholders',
            'Dynamic contract assembly with embedded compliance',
            'Intelligent escrow management with milestone releases',
            'Automated dispute resolution through AI mediation'
          ]
        },
        {
          title: 'Predictive Intelligence',
          description: '98% accurate forecasting enables proactive optimization across all business operations.',
          capabilities: [
            'Market trend prediction with real-time adaptation',
            'Demand forecasting for inventory optimization',
            'Risk prediction with automatic mitigation',
            'Performance optimization recommendations'
          ]
        }
      ]
    },
    marketplace: {
      title: 'Autonomous Marketplace Creation',
      subtitle: 'Industry-Specific Digital Ecosystems',
      description: 'AI-powered marketplace generation that creates specialized ecosystems for any industry.',
      icon: Globe,
      features: [
        {
          title: 'Industry-Specific Intelligence',
          description: 'AI automatically creates specialized marketplaces for construction, manufacturing, healthcare, and more.',
          capabilities: [
            'Automated marketplace generation for any industry vertical',
            'Dynamic user experience optimization for different user types',
            'Intelligent product categorization with 99.9% accuracy',
            'Real-time personalization based on role and behavior'
          ]
        },
        {
          title: 'Advanced Credibility Systems',
          description: 'Blockchain-verified trust networks with intelligent reputation management.',
          capabilities: [
            'Blockchain-verified credentials and certifications',
            'AI-powered reputation scoring across multiple metrics',
            'Automated trust token generation from multiple sources',
            'Predictive reliability scoring based on performance'
          ]
        },
        {
          title: 'Universal Integration',
          description: 'Seamless connection capabilities that surpass traditional platform limitations.',
          capabilities: [
            'Universal system translation between any business formats',
            'Real-time data synchronization across unlimited platforms',
            'Intelligent API orchestration with automatic updates',
            'Blockchain-native operations with smart contract compliance'
          ]
        }
      ]
    },
    financial: {
      title: 'Financial Orchestration Suite',
      subtitle: 'Embedded Banking & Finance Automation',
      description: 'Complete financial ecosystem integration with autonomous credit decisions and risk management.',
      icon: TrendingUp,
      features: [
        {
          title: 'Embedded Banking Integration',
          description: 'Direct integration with global banking systems for instant financial services.',
          capabilities: [
            'Instant credit decisions in 60 seconds vs. days',
            'Multi-currency operations with automatic hedging',
            'Real-time payment processing across global networks',
            'Automated compliance with international regulations'
          ]
        },
        {
          title: 'Intelligent Risk Management',
          description: 'AI-powered risk assessment and mitigation across all transaction types.',
          capabilities: [
            'Real-time risk monitoring across entire ecosystems',
            'Predictive default prevention with automatic interventions',
            'Dynamic credit limit adjustments based on performance',
            'Automated risk distribution across multiple institutions'
          ]
        },
        {
          title: 'Supply Chain Finance',
          description: 'Complete financing orchestration across complex supply chains.',
          capabilities: [
            'Purchase order financing with automatic approval',
            'Inventory financing based on real-time demand',
            'Working capital optimization for all transaction parties',
            'Invoice factoring with intelligent timing optimization'
          ]
        }
      ]
    },
    analytics: {
      title: 'Predictive Analytics & Intelligence',
      subtitle: 'Enterprise Business Intelligence',
      description: 'Advanced analytics that provide actionable insights and automatic optimization recommendations.',
      icon: Layers,
      features: [
        {
          title: 'Real-Time Performance Dashboards',
          description: 'Comprehensive view of operations across unlimited partners and channels.',
          capabilities: [
            'Real-time performance monitoring across all operations',
            'Customizable dashboards for different stakeholder needs',
            'Automated alert systems for critical business metrics',
            'Cross-platform data consolidation and visualization'
          ]
        },
        {
          title: 'Predictive Market Intelligence',
          description: 'AI forecasts market trends with 98% accuracy for strategic planning.',
          capabilities: [
            'Market trend forecasting with high accuracy predictions',
            'Competitive analysis with automatic strategy recommendations',
            'Customer behavior prediction and optimization',
            'Strategic planning support with scenario modeling'
          ]
        },
        {
          title: 'Automated Optimization',
          description: 'Continuous process improvement without human intervention.',
          capabilities: [
            'Autonomous process optimization with measurable results',
            'Resource allocation optimization based on real-time priorities',
            'Performance bottleneck identification and resolution',
            'Cost optimization recommendations with impact analysis'
          ]
        }
      ]
    }
  };

  const productTabs = [
    { id: 'platform', label: 'AI Platform', icon: Brain },
    { id: 'marketplace', label: 'Marketplace', icon: Globe },
    { id: 'financial', label: 'Financial Suite', icon: TrendingUp },
    { id: 'analytics', label: 'Analytics', icon: Layers }
  ];

  const currentProduct = products[activeProduct];
  const IconComponent = currentProduct.icon;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Enterprise Products
          </h1>
          <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-4xl mx-auto">
            Comprehensive suite of AI-driven products that revolutionize enterprise commerce operations through intelligent automation.
          </p>
        </div>
      </section>

      {/* Product Navigation */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center mb-16 border-b border-gray-200">
            {productTabs.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveProduct(tab.id)}
                  className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 mr-8 mb-4 transition-colors duration-200 ${
                    activeProduct === tab.id
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-purple-600 hover:border-purple-300'
                  }`}
                >
                  <TabIcon className="w-5 h-5 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Active Product Content */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mb-6">
                <IconComponent className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {currentProduct.title}
              </h2>
              <p className="text-xl text-purple-600 font-semibold mb-4">
                {currentProduct.subtitle}
              </p>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {currentProduct.description}
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid lg:grid-cols-3 gap-8">
              {currentProduct.features.map((feature, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 mb-6">{feature.description}</p>
                  <ul className="space-y-3">
                    {feature.capabilities.map((capability, capIndex) => (
                      <li key={capIndex} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{capability}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integration Capabilities */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Enterprise Integration Excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Seamless integration with existing enterprise systems and third-party services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'ERP Systems',
                description: 'SAP, Oracle, Microsoft Dynamics',
                icon: Layers
              },
              {
                title: 'Financial Systems',
                description: 'Global banking and payment networks',
                icon: TrendingUp
              },
              {
                title: 'Security & Compliance',
                description: 'SOC 2, GDPR, PCI DSS certified',
                icon: Shield
              },
              {
                title: 'API Ecosystem',
                description: 'RESTful APIs with GraphQL support',
                icon: Zap
              }
            ].map((integration, index) => {
              const IntegrationIcon = integration.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow duration-300">
                  <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mb-4">
                    <IntegrationIcon className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{integration.title}</h3>
                  <p className="text-gray-600 text-sm">{integration.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Enterprise-Grade Architecture
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Built for scale, security, and reliability with 99.99% uptime guarantee.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">99.99%</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">Uptime SLA</div>
              <div className="text-gray-600">Enterprise-grade reliability with global redundancy</div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">&lt; 50ms</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">API Response</div>
              <div className="text-gray-600">Lightning-fast processing for real-time operations</div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">1M+</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">Transactions/Day</div>
              <div className="text-gray-600">Scalable architecture handling massive volumes</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Experience the Platform
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
            See how our enterprise products can transform your operations with a personalized demonstration.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-purple-600 hover:bg-purple-50 font-semibold text-lg px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
            >
              Schedule Product Demo
              <ArrowRight className="ml-3 h-5 w-5" />
            </Link>
            <Link
              to="/pricing"
              className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 inline-flex items-center justify-center backdrop-blur-sm"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
